import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import apiClient from '../services/apiClient';
import { RESERVED_ORG_CODES } from '../App';

const STEP_EMAIL = 'email';
const STEP_OTP = 'otp';
const STEP_PASSWORD = 'password';
const STEP_DONE = 'done';

const MODE_PASSWORD = 'password';   // Forgot password flow
const MODE_USERNAME = 'username';   // Forgot username flow

function ForgotPassword() {
  const { t } = useTranslation();
  const { orgCode: rawOrgCode } = useParams();
  // A bare `/forgot-password` also matches the app's `/:orgCode/*` route,
  // with "forgot-password" itself captured as orgCode — not a real org.
  const orgCode = rawOrgCode && !RESERVED_ORG_CODES.includes(rawOrgCode.toLowerCase()) ? rawOrgCode : undefined;
  const orgPrefix = orgCode ? `/${orgCode}` : '';

  const [mode, setMode] = useState(MODE_PASSWORD);
  const [step, setStep] = useState(STEP_EMAIL);
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPassword2, setNewPassword2] = useState('');
  const [showPw1, setShowPw1] = useState(false);
  const [showPw2, setShowPw2] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const inputClass =
    'w-full px-4 py-3 border border-slate-200 rounded-xl text-[15px] bg-slate-50 ' +
    'transition-all duration-200 focus:outline-none focus:border-slate-900 focus:bg-white ' +
    'focus:ring-4 focus:ring-slate-900/10 hover:border-slate-300 ' +
    'disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed placeholder:text-slate-400';

  const startCooldown = () => {
    setCooldown(60);
    const timer = setInterval(() => {
      setCooldown(prev => {
        if (prev <= 1) { clearInterval(timer); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const switchMode = (newMode) => {
    setMode(newMode);
    setStep(STEP_EMAIL);
    setEmail('');
    setOtp('');
    setResetToken('');
    setNewPassword('');
    setNewPassword2('');
    setError('');
    setSuccess('');
    setCooldown(0);
  };

  // ── Shared: send email ─────────────────────────────────────────
  const handleSendEmail = async (e) => {
    e.preventDefault();
    if (!email.trim()) { setError(t('forgotPassword.errorEnterEmail')); return; }
    setError('');
    setLoading(true);
    try {
      if (mode === MODE_PASSWORD) {
        await apiClient.post('/auth/forgot_password/', { email: email.trim().toLowerCase() });
        setStep(STEP_OTP);
        startCooldown();
      } else {
        const res = await apiClient.post('/auth/forgot_username/', { email: email.trim().toLowerCase() });
        setSuccess(res.data.message);
        setStep(STEP_DONE);
      }
    } catch (err) {
      setError(err.response?.data?.error || t('forgotPassword.errorGeneric'));
    } finally {
      setLoading(false);
    }
  };

  // ── Password flow: verify OTP ──────────────────────────────────
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) { setError(t('forgotPassword.errorEnterCode')); return; }
    setError('');
    setLoading(true);
    try {
      const res = await apiClient.post('/auth/verify_reset_otp/', {
        email: email.trim().toLowerCase(),
        code: otp,
      });
      setResetToken(res.data.reset_token);
      setStep(STEP_PASSWORD);
    } catch (err) {
      setError(err.response?.data?.error || t('forgotPassword.errorInvalidCode'));
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (cooldown > 0) return;
    setError('');
    setLoading(true);
    try {
      await apiClient.post('/auth/forgot_password/', { email: email.trim().toLowerCase() });
      setOtp('');
      startCooldown();
    } catch (err) {
      setError(err.response?.data?.error || t('forgotPassword.errorResendFailed'));
    } finally {
      setLoading(false);
    }
  };

  // ── Password flow: set new password ───────────────────────────
  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== newPassword2) { setError(t('forgotPassword.errorPasswordMismatch')); return; }
    if (newPassword.length < 8) { setError(t('forgotPassword.errorPasswordLength')); return; }
    setError('');
    setLoading(true);
    try {
      const res = await apiClient.post('/auth/reset_password/', {
        email: email.trim().toLowerCase(),
        reset_token: resetToken,
        new_password: newPassword,
        new_password2: newPassword2,
      });
      setSuccess(res.data.message);
      setStep(STEP_DONE);
    } catch (err) {
      setError(err.response?.data?.error || t('forgotPassword.errorResetFailed'));
    } finally {
      setLoading(false);
    }
  };

  // ── Render helpers ─────────────────────────────────────────────
  const StepIndicator = () => {
    if (mode === MODE_USERNAME) return null;
    const steps = [
      { key: STEP_EMAIL, label: t('forgotPassword.stepEmail') },
      { key: STEP_OTP, label: t('forgotPassword.stepVerify') },
      { key: STEP_PASSWORD, label: t('forgotPassword.stepNewPassword') },
    ];
    const currentIdx = steps.findIndex(s => s.key === step);
    return (
      <div className="flex items-center justify-center gap-2 mb-8">
        {steps.map((s, idx) => (
          <React.Fragment key={s.key}>
            <div className="flex flex-col items-center gap-1">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all
                ${idx < currentIdx ? 'bg-green-500 text-white' : idx === currentIdx ? 'bg-slate-900 text-white' : 'bg-slate-200 text-slate-400'}`}>
                {idx < currentIdx ? '✓' : idx + 1}
              </div>
              <span className={`text-xs font-medium ${idx === currentIdx ? 'text-slate-900' : 'text-slate-400'}`}>{s.label}</span>
            </div>
            {idx < steps.length - 1 && (
              <div className={`h-0.5 w-10 mb-5 rounded transition-all ${idx < currentIdx ? 'bg-green-400' : 'bg-slate-200'}`} />
            )}
          </React.Fragment>
        ))}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 overflow-y-auto bg-gradient-to-br from-[#0f172a] to-[#1e293b] z-10">
      <div className="flex min-h-full items-center justify-center p-5">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md px-10 py-12 border border-white/10 animate-slide-up">

          {/* Header */}
          <div className="text-center mb-8">
            <div className="text-5xl mb-3">{mode === MODE_PASSWORD ? '🔑' : '👤'}</div>
            <h2 className="text-[1.75rem] font-bold tracking-tight text-slate-900 mb-1">
              {mode === MODE_PASSWORD ? t('forgotPassword.resetPasswordTitle') : t('forgotPassword.forgotUsernameTitle')}
            </h2>
            <p className="text-sm font-medium text-slate-500">
              {mode === MODE_PASSWORD
                ? t('forgotPassword.resetPasswordSubtitle')
                : t('forgotPassword.forgotUsernameSubtitle')}
            </p>
          </div>

          {/* Mode toggle tabs */}
          {step === STEP_EMAIL && (
            <div className="flex rounded-xl border border-slate-200 mb-6 overflow-hidden">
              <button
                onClick={() => switchMode(MODE_PASSWORD)}
                className={`flex-1 py-2.5 text-sm font-semibold transition-all
                  ${mode === MODE_PASSWORD ? 'bg-slate-900 text-white' : 'bg-white text-slate-500 hover:bg-slate-50'}`}
              >
                {t('forgotPassword.forgotPasswordTab')}
              </button>
              <button
                onClick={() => switchMode(MODE_USERNAME)}
                className={`flex-1 py-2.5 text-sm font-semibold transition-all
                  ${mode === MODE_USERNAME ? 'bg-slate-900 text-white' : 'bg-white text-slate-500 hover:bg-slate-50'}`}
              >
                {t('forgotPassword.forgotUsernameTab')}
              </button>
            </div>
          )}

          {/* Step indicator (password flow only) */}
          {step !== STEP_DONE && <StepIndicator />}

          {/* Error */}
          {error && (
            <div className="mb-5 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm font-medium">
              {error}
            </div>
          )}

          {/* ── STEP: Email ── */}
          {step === STEP_EMAIL && (
            <form onSubmit={handleSendEmail} className="flex flex-col gap-5">
              <div className="flex flex-col gap-2">
                <label htmlFor="email" className="text-sm font-semibold text-slate-800">
                  {t('register.emailAddress')}
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={e => { setEmail(e.target.value); setError(''); }}
                  placeholder={t('register.emailPlaceholder')}
                  required
                  disabled={loading}
                  className={inputClass}
                />
              </div>
              <button
                type="submit"
                disabled={loading || !email.trim()}
                className="mt-1 w-full py-3 px-5 text-base font-bold text-white rounded-xl
                  bg-gradient-to-br from-slate-900 to-slate-700 shadow-md
                  transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl
                  active:translate-y-0 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {loading ? t('forgotPassword.sending') : mode === MODE_PASSWORD ? t('forgotPassword.sendResetCode') : t('forgotPassword.sendMyUsername')}
              </button>
            </form>
          )}

          {/* ── STEP: OTP verification ── */}
          {step === STEP_OTP && (
            <form onSubmit={handleVerifyOtp} className="flex flex-col gap-5">
              <div className="px-4 py-3 rounded-xl bg-blue-50 border border-blue-200 text-blue-700 text-sm">
                {t('forgotPassword.codeSentTo', { email })}
              </div>
              <div className="flex flex-col gap-2">
                <label htmlFor="otp" className="text-sm font-semibold text-slate-800">{t('forgotPassword.resetCode')}</label>
                <input
                  type="text"
                  id="otp"
                  value={otp}
                  onChange={e => { setOtp(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(''); }}
                  placeholder="123456"
                  inputMode="numeric"
                  maxLength={6}
                  required
                  disabled={loading}
                  className={inputClass + ' text-center text-2xl tracking-[0.5em] font-bold'}
                />
              </div>
              <button
                type="submit"
                disabled={loading || otp.length !== 6}
                className="mt-1 w-full py-3 px-5 text-base font-bold text-white rounded-xl
                  bg-gradient-to-br from-slate-900 to-slate-700 shadow-md
                  transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl
                  active:translate-y-0 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {loading ? t('register.verifying') : t('register.verifyCode')}
              </button>
              <div className="text-center text-sm text-slate-500">
                {t('register.didNotReceive')}{' '}
                <button
                  type="button"
                  onClick={handleResendOtp}
                  disabled={cooldown > 0 || loading}
                  className="text-slate-900 font-bold disabled:text-slate-400 disabled:cursor-not-allowed hover:underline"
                >
                  {cooldown > 0 ? t('register.resendIn', { seconds: cooldown }) : t('register.resend')}
                </button>
              </div>
              <button
                type="button"
                onClick={() => { setStep(STEP_EMAIL); setOtp(''); setError(''); }}
                className="text-sm text-slate-500 underline text-center"
              >
                {t('forgotPassword.changeEmailBack')}
              </button>
            </form>
          )}

          {/* ── STEP: New password ── */}
          {step === STEP_PASSWORD && (
            <form onSubmit={handleResetPassword} className="flex flex-col gap-5">
              <div className="flex flex-col gap-2">
                <label htmlFor="new_password" className="text-sm font-semibold text-slate-800">{t('forgotPassword.newPassword')}</label>
                <div className="relative">
                  <input
                    type={showPw1 ? 'text' : 'password'}
                    id="new_password"
                    value={newPassword}
                    onChange={e => { setNewPassword(e.target.value); setError(''); }}
                    placeholder={t('forgotPassword.passwordMinPlaceholder')}
                    required
                    disabled={loading}
                    className={inputClass + ' pr-12'}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw1(p => !p)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 text-lg"
                    tabIndex={-1}
                  >
                    {showPw1 ? '🙈' : '👁'}
                  </button>
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <label htmlFor="new_password2" className="text-sm font-semibold text-slate-800">{t('forgotPassword.confirmNewPassword')}</label>
                <div className="relative">
                  <input
                    type={showPw2 ? 'text' : 'password'}
                    id="new_password2"
                    value={newPassword2}
                    onChange={e => { setNewPassword2(e.target.value); setError(''); }}
                    placeholder={t('forgotPassword.repeatPasswordPlaceholder')}
                    required
                    disabled={loading}
                    className={inputClass + ' pr-12'}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw2(p => !p)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 text-lg"
                    tabIndex={-1}
                  >
                    {showPw2 ? '🙈' : '👁'}
                  </button>
                </div>
                {newPassword2 && newPassword !== newPassword2 && (
                  <p className="text-xs text-red-600 font-medium">{t('forgotPassword.passwordsDontMatch')}</p>
                )}
              </div>
              <button
                type="submit"
                disabled={loading || !newPassword || !newPassword2 || newPassword !== newPassword2}
                className="mt-1 w-full py-3 px-5 text-base font-bold text-white rounded-xl
                  bg-gradient-to-br from-slate-900 to-slate-700 shadow-md
                  transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl
                  active:translate-y-0 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {loading ? t('forgotPassword.saving') : t('forgotPassword.setNewPassword')}
              </button>
            </form>
          )}

          {/* ── STEP: Done ── */}
          {step === STEP_DONE && (
            <div className="flex flex-col items-center gap-5 text-center">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center text-3xl">
                ✅
              </div>
              <p className="text-slate-700 text-sm font-medium leading-relaxed">
                {success}
              </p>
              <Link
                to={`${orgPrefix}/login`}
                className="mt-2 w-full py-3 px-5 text-base font-bold text-white rounded-xl text-center
                  bg-gradient-to-br from-slate-900 to-slate-700 shadow-md block
                  transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl"
              >
                {t('forgotPassword.backToLogin')}
              </Link>
            </div>
          )}

          {/* Footer link */}
          {step !== STEP_DONE && (
            <div className="text-center mt-6 pt-6 border-t border-slate-100">
              <p className="text-sm text-slate-500">
                {t('forgotPassword.rememberCredentials')}{' '}
                <Link to={`${orgPrefix}/login`} className="text-slate-900 font-bold hover:underline">
                  {t('forgotPassword.signIn')}
                </Link>
              </p>
            </div>
          )}

          {/* Powered by */}
          <p className="text-center text-xs text-slate-400 mt-6">
            {t('forgotPassword.poweredBy')} <span className="font-semibold text-slate-500">Elysian Systems</span>
          </p>
        </div>
      </div>
    </div>
  );
}

export default ForgotPassword;
